import '../AdminDetailOverview.scss';
import './RecruitSeoReview.scss';
import './AggregatedManage.scss';
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';

// 가져온 공고 관리 (aggregated-posting-lock-design.md)
// 게시판에서 가져온 공고는 사역자에게 그대로 보이고, 지원자·관리 기능은 교회가 인수(선점)한 뒤에 열린다.
// 미선점 공고는 30일 뒤 자동 비노출 → 30일 보관 후 삭제. 이 화면에서 상태별 공고, 인수·삭제 수동 검토, 게시판 설정, 주간 지표를 본다.

type Tab = 'posts' | 'requests' | 'boards' | 'metrics';
type PostStatus = 'UNCLAIMED' | 'CLAIM_PENDING' | 'CLAIMED' | 'EXPIRED' | 'REJECTED';

interface Summary {
  counts: Record<PostStatus, number>;
  pendingReview: number;
  lastExpiry: { at: string; expired: number; noticed: number; deleted: number } | null;
  mail: { enabled: boolean; sent30d: number; clicked30d: number };
  rules: { unclaimedDays: number; claimedDays: number; retentionDays: number };
}

interface PostRow {
  kind: string;
  id: number;
  title: string;
  church: string;
  sourceBoard: string;
  sourceUrl: string;
  contactEmail: string | null;
  claimStatus: string;
  claimedBy: string | null;
  claimedAt: string | null;
  importedAt: string | null;
  expiresAt: string | null;
  unpublishedAt: string | null;
  unpublishReason: string | null;
  applyMode: string;
  viewCount: number;
  interestCount: number;
}

interface RequestRow {
  id: number;
  purpose: 'CLAIM' | 'DELETE';
  kind: string;
  recruitId: number;
  church: string;
  title: string;
  knownEmail: string | null;
  account: string | null;
  email: string;
  method: string;
  status: string;
  memo: string | null;
  reviewedBy: string | null;
  createdAt: string;
  doneAt: string | null;
}

interface BoardRow {
  sourceBoard: string;
  imported: number;
  claimed: number;
  rejected: number;
  emailOptOut: boolean;
  note: string;
}

interface WeekRow {
  weekStart: string;
  imported: number;
  newImported: number;
  newOriginal: number;
  claimRate: number | null;
  originalShare: number | null;
  abandonRate: number | null;
}

interface Metrics {
  weeks: WeekRow[];
  repostRate: number | null;
  churches: number;
  repeatChurches: number;
  verdict: { level: 'GREEN' | 'YELLOW' | 'RED' | 'NEUTRAL' | 'NONE'; message: string };
}

const STATUS_TABS: [PostStatus, string][] = [
  ['UNCLAIMED', '미선점'],
  ['CLAIM_PENDING', '인증 대기'],
  ['CLAIMED', '선점 완료'],
  ['EXPIRED', '만료(비노출)'],
  ['REJECTED', '삭제 요청'],
];
const KIND_LABEL: Record<string, string> = { minister: '국내', overseas: '해외', church: '찬양대/직원', institute: '기관' };
const MODE_LABEL: Record<string, string> = { RESERVE: '예약 지원', EXTERNAL: '원문 지원', ONSITE: '즉시 지원' };
const METHOD_LABEL: Record<string, string> = { EXACT: '같은 주소', DOMAIN: '같은 도메인', REVIEW: '수동 검토', OPERATOR: '운영자' };
const REQ_STATUS_LABEL: Record<string, string> = { SENT: '메일 발송', REVIEW: '검토 대기', DONE: '완료', DENIED: '반려', CLOSED: '종료', EXPIRED: '만료' };
const pct = (v: number | null) => (v == null ? '-' : `${v}%`);

export default function AggregatedManage() {
  const [tab, setTab] = useState<Tab>('posts');
  const [summary, setSummary] = useState<Summary | null>(null);

  const loadSummary = useCallback(() => {
    axios.get(`${MainURL}/aggregatedadmin/summary`).then(res => setSummary(res.data?.success ? res.data : null)).catch(() => setSummary(null));
  }, []);
  useEffect(loadSummary, [loadSummary]);

  return (
    <div className="recruit-seo-review service-detail-overview agg-admin">
      <p className="service-detail-overview__hint">
        게시판에서 가져온 공고는 <b>개수는 풀고 통제는 잠급니다</b> — 사역자에게는 그대로 보이고, 지원자·관리 기능은 교회가 인수한 뒤에 열립니다.
        미선점 공고는 {summary?.rules?.unclaimedDays ?? 30}일 뒤 자동 비노출, 이후 {summary?.rules?.retentionDays ?? 30}일 보관 후 삭제됩니다.
        (잠금은 도입 이후 등록된 공고부터 적용)
      </p>

      {summary && (
        <div className="agg-admin__summary">
          {STATUS_TABS.map(([key, label]) => (
            <div key={key}><span>{label}</span><b>{summary.counts[key]}</b></div>
          ))}
          <div><span>검토 대기</span><b className={summary.pendingReview ? 'is-alert' : ''}>{summary.pendingReview}</b></div>
          <div>
            <span>인수 유도 메일</span>
            <b>{summary.mail.enabled ? 'ON' : 'OFF'}</b>
            <small>30일 발송 {summary.mail.sent30d} · 클릭 {summary.mail.clicked30d}</small>
          </div>
          <div>
            <span>마지막 만료 배치</span>
            <small>
              {summary.lastExpiry
                ? `${summary.lastExpiry.at.slice(0, 16).replace('T', ' ')} · 비노출 ${summary.lastExpiry.expired} · 삭제 ${summary.lastExpiry.deleted}`
                : '아직 없음'}
            </small>
          </div>
        </div>
      )}

      <div className="service-detail-overview__tabs">
        {([['posts', '공고'], ['requests', '인수·삭제 검토'], ['boards', '게시판 설정'], ['metrics', '지표']] as [Tab, string][]).map(([key, label]) => (
          <button
            type="button"
            key={key}
            className={`service-detail-overview__tab${tab === key ? ' is-active' : ''}`}
            onClick={() => setTab(key)}
          >
            {label}
            {key === 'requests' && summary?.pendingReview ? ` (${summary.pendingReview})` : ''}
          </button>
        ))}
      </div>

      {tab === 'posts' && <PostsTab onChanged={loadSummary} />}
      {tab === 'requests' && <RequestsTab onChanged={loadSummary} />}
      {tab === 'boards' && <BoardsTab />}
      {tab === 'metrics' && <MetricsTab onChanged={loadSummary} mailEnabled={Boolean(summary?.mail.enabled)} />}
    </div>
  );
}

function PostsTab({ onChanged }: { onChanged: () => void }) {
  const [status, setStatus] = useState<PostStatus>('UNCLAIMED');
  const [items, setItems] = useState<PostRow[]>([]);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(30);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${MainURL}/aggregatedadmin/list`, { params: { status, page, q: query } });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setPageSize(res.data.pageSize || 30);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || '목록을 불러오지 못했습니다.');
    }
  }, [status, page, query]);
  useEffect(() => { load(); }, [load]);

  const act = async (row: PostRow, action: 'restore' | 'reject' | 'extend' | 'claim') => {
    const key = `${row.kind}-${row.id}`;
    if (busy) return;
    let body: Record<string, unknown> = {};
    if (action === 'restore' && !window.confirm(`'${row.church}' 공고를 다시 30일 게시할까요?`)) return;
    if (action === 'reject') {
      const memo = window.prompt('삭제(비노출) 사유 — 전화·메일 회신 등. 이 교회는 재수집 금지 목록에 올라갑니다.', '담당자 요청');
      if (memo == null) return;
      body = { memo, email: row.contactEmail || '' };
    }
    if (action === 'extend') {
      const days = window.prompt('며칠 연장할까요?', '30');
      if (!days) return;
      body = { days: Number(days) };
    }
    if (action === 'claim') {
      const account = window.prompt('전화로 담당자를 확인한 뒤, 연결할 교회 계정(userAccount)을 입력하세요.');
      if (!account) return;
      body = { account: account.trim() };
    }
    setBusy(key);
    try {
      await axios.post(`${MainURL}/aggregatedadmin/post/${row.kind}/${row.id}/${action}`, body);
      await load();
      onChanged();
    } catch (err: any) {
      alert(err.response?.data?.error || '처리하지 못했습니다.');
    } finally {
      setBusy('');
    }
  };

  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <div className="agg-admin__chips">
        {STATUS_TABS.map(([key, label]) => (
          <button type="button" key={key} className={status === key ? 'is-active' : ''} onClick={() => { setStatus(key); setPage(1); }}>
            {label}
          </button>
        ))}
      </div>
      <form className="church-verify__search" onSubmit={(e) => { e.preventDefault(); setQuery(search.trim()); setPage(1); }}>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="교회명 · 공고 제목" />
        <button type="submit">검색</button>
      </form>
      {error && <p className="church-verify__empty">{error}</p>}
      {!error && items.length === 0 && <p className="church-verify__empty">공고가 없습니다.</p>}

      <div className="service-detail-overview__table-x">
        <table className="church-verify__table">
          <thead>
            <tr>
              <th>공고</th>
              <th>출처</th>
              <th>성과</th>
              <th>지원 방식</th>
              <th>일정</th>
              <th>처리</th>
            </tr>
          </thead>
          <tbody>
            {items.map(row => {
              const key = `${row.kind}-${row.id}`;
              return (
                <tr key={key}>
                  <td>
                    <a href={`/recruit/${row.kind}/${row.id}`} target="_blank" rel="noopener noreferrer">{row.church || '(교회명 없음)'}</a>
                    <div className="church-verify__sub">{KIND_LABEL[row.kind]} · {row.title}</div>
                    {row.contactEmail && <div className="church-verify__sub">담당자 {row.contactEmail}</div>}
                    {row.claimedBy && <div className="church-verify__sub">인수 계정 {row.claimedBy}</div>}
                  </td>
                  <td>
                    {row.sourceUrl ? <a href={row.sourceUrl} target="_blank" rel="noopener noreferrer">{row.sourceBoard}</a> : row.sourceBoard}
                  </td>
                  <td>조회 {row.viewCount}<div className="church-verify__sub">관심 {row.interestCount}</div></td>
                  <td>{MODE_LABEL[row.applyMode] || row.applyMode}</td>
                  <td>
                    <div className="church-verify__sub">가져옴 {row.importedAt || '-'}</div>
                    {row.claimedAt && <div className="church-verify__sub">인수 {row.claimedAt}</div>}
                    <div className="church-verify__sub">만료 {row.expiresAt || '-'}</div>
                    {row.unpublishedAt && <div className="church-verify__sub">비노출 {row.unpublishedAt} ({row.unpublishReason})</div>}
                  </td>
                  <td className="agg-admin__actions">
                    {(status === 'UNCLAIMED' || status === 'CLAIM_PENDING') && (
                      <>
                        <button type="button" className="church-verify__toggle" disabled={busy === key} onClick={() => act(row, 'claim')}>수동 인수</button>
                        <button type="button" className="church-verify__toggle" disabled={busy === key} onClick={() => act(row, 'extend')}>연장</button>
                      </>
                    )}
                    {(status === 'EXPIRED' || status === 'REJECTED') && (
                      <button type="button" className="church-verify__toggle" disabled={busy === key} onClick={() => act(row, 'restore')}>복원</button>
                    )}
                    {status === 'EXPIRED' && (
                      <button type="button" className="church-verify__toggle" disabled={busy === key} onClick={() => act(row, 'claim')}>수동 인수</button>
                    )}
                    {status !== 'REJECTED' && (
                      <button type="button" className="church-verify__toggle agg-admin__danger" disabled={busy === key} onClick={() => act(row, 'reject')}>삭제 처리</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="church-verify__pager">
        <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>이전</button>
        <span>{page} / {lastPage} · 총 {total}건</span>
        <button type="button" disabled={page >= lastPage} onClick={() => setPage(page + 1)}>다음</button>
      </div>
    </>
  );
}

function RequestsTab({ onChanged }: { onChanged: () => void }) {
  const [filter, setFilter] = useState<'REVIEW' | 'SENT' | 'ALL'>('REVIEW');
  const [items, setItems] = useState<RequestRow[]>([]);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(() => {
    axios.get(`${MainURL}/aggregatedadmin/requests`, { params: { status: filter === 'ALL' ? undefined : filter } })
      .then(res => setItems(res.data.items || []))
      .catch(() => setItems([]));
  }, [filter]);
  useEffect(load, [load]);

  const decide = async (row: RequestRow, approve: boolean) => {
    if (busyId) return;
    const what = row.purpose === 'CLAIM' ? `${row.account} 계정에 인수` : '즉시 비노출(삭제)';
    const memo = window.prompt(approve ? `전화 확인 후 ${what} 처리합니다. 메모(선택)` : '반려 사유(선택)', '');
    if (memo == null) return;
    setBusyId(row.id);
    try {
      await axios.post(`${MainURL}/aggregatedadmin/requests/${row.id}/${approve ? 'approve' : 'deny'}`, { memo });
      load();
      onChanged();
    } catch (err: any) {
      alert(err.response?.data?.error || '처리하지 못했습니다.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <p className="service-detail-overview__hint">
        게시판에 등록된 담당자 이메일과 다른 주소로 들어온 요청입니다. <b>담당자에게 전화로 확인</b>한 뒤 승인해 주세요. 삭제 요청은 접수 후 24시간 이내 처리가 목표입니다.
      </p>
      <div className="agg-admin__chips">
        {([['REVIEW', '검토 대기'], ['SENT', '인증 메일 발송'], ['ALL', '전체']] as const).map(([key, label]) => (
          <button type="button" key={key} className={filter === key ? 'is-active' : ''} onClick={() => setFilter(key)}>{label}</button>
        ))}
      </div>
      {items.length === 0 && <p className="church-verify__empty">요청이 없습니다.</p>}
      <div className="service-detail-overview__table-x">
        <table className="church-verify__table">
          <thead>
            <tr>
              <th>요청</th>
              <th>공고</th>
              <th>요청자</th>
              <th>게시판 담당자</th>
              <th>상태</th>
              <th>처리</th>
            </tr>
          </thead>
          <tbody>
            {items.map(row => (
              <tr key={row.id}>
                <td>{row.purpose === 'CLAIM' ? '인수' : '삭제'}<div className="church-verify__sub">{row.createdAt}</div></td>
                <td>
                  <a href={`/recruit/${row.kind}/${row.recruitId}`} target="_blank" rel="noopener noreferrer">{row.church}</a>
                  <div className="church-verify__sub">{row.title}</div>
                </td>
                <td>
                  {row.email}
                  {row.account && <div className="church-verify__sub">계정 {row.account}</div>}
                  <div className="church-verify__sub">{METHOD_LABEL[row.method] || row.method}</div>
                </td>
                <td>{row.knownEmail || '-'}</td>
                <td>
                  {REQ_STATUS_LABEL[row.status] || row.status}
                  {row.reviewedBy && <div className="church-verify__sub">{row.reviewedBy}</div>}
                  {row.memo && <div className="church-verify__sub">{row.memo}</div>}
                </td>
                <td className="agg-admin__actions">
                  {(row.status === 'REVIEW' || row.status === 'SENT') && (
                    <>
                      <button type="button" className="church-verify__toggle" disabled={busyId === row.id} onClick={() => decide(row, true)}>승인</button>
                      <button type="button" className="church-verify__toggle" disabled={busyId === row.id} onClick={() => decide(row, false)}>반려</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function BoardsTab() {
  const [items, setItems] = useState<BoardRow[]>([]);
  const [busy, setBusy] = useState('');

  const load = useCallback(() => {
    axios.get(`${MainURL}/aggregatedadmin/boards`).then(res => setItems(res.data.items || [])).catch(() => setItems([]));
  }, []);
  useEffect(load, [load]);

  const toggle = async (row: BoardRow) => {
    if (busy) return;
    const next = !row.emailOptOut;
    const note = next ? window.prompt(`'${row.sourceBoard}' 게시판에 "이메일 무단수집 거부" 표시가 있나요? 메모`, '게시판 하단 수집거부 표시') : '';
    if (next && note == null) return;
    setBusy(row.sourceBoard);
    try {
      await axios.post(`${MainURL}/aggregatedadmin/boards`, { sourceBoard: row.sourceBoard, emailOptOut: next, note });
      load();
    } catch (err: any) {
      alert(err.response?.data?.error || '저장하지 못했습니다.');
    } finally {
      setBusy('');
    }
  };

  return (
    <>
      <p className="service-detail-overview__hint">
        게시판에 <b>"이메일 무단수집 거부"</b> 표시가 있으면 수집거부로 설정하세요 (정보통신망법 제50조의2). 그 게시판 공고는 인수 유도 메일을 보내지 않고,
        교회 이메일로 지원을 전달하지 않습니다 (원문 지원으로 안내).
      </p>
      {items.length === 0 && <p className="church-verify__empty">아직 가져온 공고가 없습니다.</p>}
      <table className="church-verify__table">
        <thead>
          <tr>
            <th>게시판</th>
            <th>가져옴</th>
            <th>선점</th>
            <th>삭제 요청</th>
            <th>선점율</th>
            <th>이메일 수집거부</th>
          </tr>
        </thead>
        <tbody>
          {items.map(row => (
            <tr key={row.sourceBoard}>
              <td>{row.sourceBoard}{row.note && <div className="church-verify__sub">{row.note}</div>}</td>
              <td>{row.imported}</td>
              <td>{row.claimed}</td>
              <td>{row.rejected}</td>
              <td>{row.imported ? `${Math.round((row.claimed / row.imported) * 1000) / 10}%` : '-'}</td>
              <td>
                <button type="button" className={`church-verify__toggle${row.emailOptOut ? ' is-on' : ''}`} disabled={busy === row.sourceBoard} onClick={() => toggle(row)}>
                  {row.emailOptOut ? '수집거부' : '허용'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function MetricsTab({ onChanged, mailEnabled }: { onChanged: () => void; mailEnabled: boolean }) {
  const [data, setData] = useState<Metrics | null>(null);
  const [running, setRunning] = useState('');

  const load = useCallback(() => {
    axios.get(`${MainURL}/aggregatedadmin/metrics`).then(res => setData(res.data?.success ? res.data : null)).catch(() => setData(null));
  }, []);
  useEffect(load, [load]);

  const run = async (what: 'expiry' | 'mails') => {
    if (running) return;
    if (!window.confirm(what === 'expiry' ? '만료 배치를 지금 실행할까요? (만료된 미선점 공고 비노출 · 30일 지난 공고 삭제)' : '인수 유도 메일을 지금 발송할까요?')) return;
    setRunning(what);
    try {
      const res = await axios.post(`${MainURL}/aggregatedadmin/run/${what}`);
      alert(what === 'expiry'
        ? `비노출 ${res.data.expired}건 · 원문 지원 안내 ${res.data.noticed}건 · 삭제 ${res.data.deleted}건`
        : `발송 ${res.data.sent ?? 0}건`);
      onChanged();
      load();
    } catch (err: any) {
      alert(err.response?.data?.error || '실행하지 못했습니다.');
    } finally {
      setRunning('');
    }
  };

  if (!data) return <p className="church-verify__empty">불러오는 중...</p>;
  const last = data.weeks[data.weeks.length - 1];
  return (
    <>
      <div className={`agg-admin__verdict agg-admin__verdict--${data.verdict.level}`}>
        <b>{data.verdict.level === 'NONE' ? '판정 보류' : data.verdict.level}</b> {data.verdict.message}
      </div>
      <div className="agg-admin__summary">
        <div><span>선점율 (누적)</span><b>{pct(last?.claimRate ?? null)}</b></div>
        <div><span>직접등록 비중 (이번 주)</span><b>{pct(last?.originalShare ?? null)}</b></div>
        <div><span>재등록률 (최종 KPI)</span><b>{pct(data.repostRate)}</b><small>{data.repeatChurches} / {data.churches}개 교회</small></div>
        <div><span>방치율</span><b>{pct(last?.abandonRate ?? null)}</b></div>
      </div>
      <table className="church-verify__table">
        <thead>
          <tr>
            <th>주 시작</th>
            <th>신규 가져옴</th>
            <th>신규 직접등록</th>
            <th>선점율</th>
            <th>직접등록 비중</th>
            <th>방치율</th>
          </tr>
        </thead>
        <tbody>
          {data.weeks.map(w => (
            <tr key={w.weekStart}>
              <td>{w.weekStart}</td>
              <td>{w.newImported}</td>
              <td>{w.newOriginal}</td>
              <td>{pct(w.claimRate)}</td>
              <td>{pct(w.originalShare)}</td>
              <td>{pct(w.abandonRate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="service-detail-overview__hint" style={{ marginTop: 12 }}>
        GREEN: 선점율·직접등록 비중 동반 상승 → 가져오기 확대 / YELLOW: 가져오기 급증 + 선점율 정체 → 게시판 품질 점검 /
        RED: 선점율·직접등록 비중 동반 하락 → 가져오기를 줄이지 말고 잠금을 강화.
      </p>
      <div className="agg-admin__chips">
        <button type="button" disabled={Boolean(running)} onClick={() => run('expiry')}>{running === 'expiry' ? '실행 중...' : '만료 배치 지금 실행'}</button>
        <button type="button" disabled={Boolean(running) || !mailEnabled} onClick={() => run('mails')} title={mailEnabled ? '' : '.env AGG_CLAIM_MAIL_ENABLED=true 일 때만'}>
          {running === 'mails' ? '발송 중...' : '인수 유도 메일 지금 발송'}
        </button>
      </div>
    </>
  );
}
